import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UtensilsCrossed, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { portalApi } from '../../../api/portal.js';
import { PlateChart } from '../../../components/PlateChart.jsx';

const MD_COMPONENTS = {
  p: ({ children }) => <p className="my-1.5 leading-relaxed first:mt-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 list-disc space-y-0.5 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <h4 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h4>,
  h2: ({ children }) => <h4 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h4>,
  h3: ({ children }) => <h4 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h4>,
  blockquote: ({ children }) => <blockquote className="my-2 border-l-2 border-portal-border pl-3 italic text-portal-ink-muted">{children}</blockquote>,
  img: ({ src, alt }) => (src && !src.startsWith('storage:') ? <img src={src} alt={alt || ''} className="my-2 max-h-72 rounded-portal" /> : null)
};

/**
 * Collapsible ORME reading: a titled section list rendered as markdown.
 */
function ReadingBlock({ title, reading }) {
  return (
    <details className="mt-3 rounded-portal border border-portal-border bg-portal-bg/40">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium text-portal-ink">
        <ChevronDown className="h-4 w-4 text-portal-ink-muted" />
        {title}
      </summary>
      <div className="space-y-3 px-4 pb-4 text-sm text-portal-ink">
        {reading.sections.map((s) => (
          <div key={s.key}>
            {s.title && <p className="text-xs font-semibold uppercase tracking-wide text-portal-ink-muted">{s.title}</p>}
            <div className="mt-0.5">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{s.text}</ReactMarkdown>
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * The "real plate" for the owner's pet: a warm visualization of the
 * formulations recommended in the pet's consultations (ORME Plate Method).
 */
export function PlatePage() {
  const { selectedPet } = useOutletContext();
  const petId = selectedPet && selectedPet._id;

  const query = useQuery({
    queryKey: ['portal', 'nutrition', petId],
    queryFn: () => portalApi.getNutrition(petId),
    enabled: Boolean(petId)
  });

  if (!petId) return null;
  const plates = (query.data && query.data.plates) || [];

  return (
    <div className="pt-2 text-portal-ink" style={{ '--plate-hole': 'var(--color-portal-card)' }}>
      <h2 className="text-2xl font-semibold">Il piatto di {selectedPet.name}</h2>
      <p className="mt-1 text-portal-ink-muted">
        Una rappresentazione di cosa contiene davvero il cibo consigliato per il tuo animale.
      </p>

      {query.isLoading && (
        <div className="mt-5 space-y-4">
          {[0, 1].map((i) => <div key={i} className="shimmer h-40 rounded-portal" />)}
        </div>
      )}

      {query.isError && (
        <p className="mt-5 text-portal-ink-muted">Non riusciamo a caricare il piatto in questo momento.</p>
      )}

      {query.isSuccess && plates.length === 0 && (
        <div className="mt-5 rounded-portal bg-portal-card p-10 text-center shadow-portal">
          <UtensilsCrossed className="mx-auto h-9 w-9 text-portal-ink-faint" strokeWidth={1.5} />
          <p className="mt-3 text-portal-ink-muted">
            Qui vedrai il piatto del cibo consigliato a {selectedPet.name}, appena avremo registrato una consulenza con un alimento dal profilo nutrizionale completo.
          </p>
        </div>
      )}

      <div className="mt-5 space-y-4">
        {plates.map((entry) => (
          <div key={entry.product.id} className="rounded-portal bg-portal-card p-5 shadow-portal">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-portal-ink">{entry.product.name}</p>
                {entry.product.brand && <p className="text-sm text-portal-ink-muted">{entry.product.brand}</p>}
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${
                entry.product.purchased ? 'bg-portal-sage-soft text-portal-sage' : 'bg-portal-accent-soft text-portal-accent'
              }`}>
                {entry.product.purchased ? 'acquistato' : 'consigliato'}
              </span>
            </div>
            <PlateChart plate={entry.plate} />

            {(entry.product.recipeReading || entry.product.plateReading) && (
              <div className="mt-4 border-t border-portal-border pt-3">
                <p className="text-sm font-semibold text-portal-ink">La ricetta secondo il Metodo ORME</p>
                {entry.product.recipeReading && <ReadingBlock title="Lettura della Ricetta" reading={entry.product.recipeReading} />}
                {entry.product.plateReading && <ReadingBlock title="Scomposizione del Piatto" reading={entry.product.plateReading} />}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
